import {useCallback,useEffect,useState} from "react";
import MetaTestReceipt from "./MetaTestReceipt";
import api,{formatApiErrorDetail} from "@/lib/api";

const field="mt-1 w-full min-w-0 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm";
const btn="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-40";
const empty={campaign_id:"",page_id:"",form_id:"",review_reference:"",sms_checkbox_key:"",sms_consent_text:"",call_checkbox_key:"",call_consent_text:""};

export default function MetaLeadIntake({campaigns=[]}){
  const [status,setStatus]=useState(null);
  const [bindings,setBindings]=useState([]);
  const [events,setEvents]=useState([]);
  const [form,setForm]=useState(empty);
  const [error,setError]=useState("");
  const [notice,setNotice]=useState("");
  const [working,setWorking]=useState("");
  const [preflight,setPreflight]=useState({});
  const [loading,setLoading]=useState(true);
  const approved=campaigns.filter(c=>["draft","active"].includes(c.status)&&c.compliance_review_attested);
  const backendOrigin=(api.defaults.baseURL||"").replace(/\/api\/?$/,"")||(typeof window!=="undefined"?window.location.origin:"");
  const refresh=useCallback(async()=>{
    const [s,b,e]=await Promise.all([
      api.get("/platform/meta-leads/status"),
      api.get("/platform/meta-leads/bindings"),
      api.get("/platform/meta-leads/events"),
    ]);
    setStatus(s.data);setBindings(b.data||[]);setEvents(e.data||[]);
  },[]);
  useEffect(()=>{refresh().catch(e=>setError(formatApiErrorDetail(e.response?.data?.detail)||
    "Could not load Meta lead-receiving status.")).finally(()=>setLoading(false));},[refresh]);

  const act=async(label,perform,success)=>{
    setWorking(label);setError("");setNotice("");
    try{await perform();setNotice(success);await refresh();}
    catch(e){setError(formatApiErrorDetail(e?.response?.data?.detail)||
      "Action unavailable. Check the reviewed campaign and Meta permissions.");}
    finally{setWorking("");}
  };
  const bind=()=>{
    if(!form.campaign_id||form.review_reference.trim().length<8||
      !/^\d{5,30}$/.test(form.page_id)||!/^\d{5,30}$/.test(form.form_id))return;
    if(!window.confirm("Bind this exact Meta Page and Lead Form to the independently reviewed Final Expense campaign? This will not launch ads or send messages."))return;
    act("bind",()=>api.post("/platform/meta-leads/bindings",form),
      "Meta form mapped. Confirm the Meta Page is subscribed to leadgen webhooks.");
  };
  const sync=b=>{
    if(!window.confirm("Retrieve up to 25 existing leads from this Meta Form? This is a read-only Meta API request; each lead enters the review queue without automatic calls, texts or resale."))return;
    act("sync-"+b.id,()=>api.post(`/platform/meta-leads/bindings/${b.id}/sync`),
      "Meta lead retrieval completed. Review the inbox and lead inventory.");
  };
  const inspectBinding=async b=>{
    setWorking("preflight-"+b.id);setError("");setNotice("");
    try{
      const {data}=await api.get(`/platform/meta-leads/bindings/${b.id}/preflight`);
      setPreflight(old=>({...old,[b.id]:data}));
      setNotice("Meta Page/Form inspection completed. This does not prove live delivery or spend any money.");
    }catch(e){setError(formatApiErrorDetail(e?.response?.data?.detail)||
      "Could not verify this Meta Page/Form. Review Page token and app subscription.");}
    finally{setWorking("");}
  };
  const retry=e=>act("retry-"+e.id,
    ()=>api.post(`/platform/meta-leads/events/${encodeURIComponent(e.id)}/retry`),
    "Import retried. Review the updated event status.");

  return <section className="space-y-4" data-testid="meta-lead-intake">
    <header className="rounded-xl border border-slate-200 bg-white p-4">
      <h3 className="font-heading text-lg font-bold">Meta Instant Forms → Fortunate Ops leads</h3>
      <p className="mt-1 text-sm text-slate-600">Receive submitted Facebook/Instagram leads directly into platform-owned inventory. No automatic outreach, lead resale, or paid advertising is enabled by this connection.</p>
      {loading?<p className="mt-2 text-sm">Checking integration…</p>:<div className="mt-3 flex flex-wrap gap-2 text-xs">
        <span className="rounded-full border px-3 py-1">Webhook: {status?.ready?"Configured":"Not ready"}</span>
        <span className="rounded-full border px-3 py-1">Live Meta retrieval: {status?.meta_live_delivery_verified?"Verified":"Not yet verified"}</span>
        <span className="rounded-full border px-3 py-1">Ads: Not activated</span>
      </div>}
      <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm">
        <strong>Webhook endpoint:</strong>
        <p className="mt-1 break-all font-mono text-xs">{backendOrigin}{status?.webhook_path||"/api/public/meta-leads/webhook"}</p>
        <p className="mt-2 text-xs text-slate-600">Configure Meta App Secret, Webhook Verify Token, and Page Access Token as deployment secrets. Subscribe the Meta app and Page to the <code>leadgen</code> webhook. Confirm <code>leads_retrieval</code> access. Do not paste credentials into this form.</p>
      </div>
    </header>
    {error&&<p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</p>}
    {notice&&<p role="status" className="rounded-lg border border-slate-200 bg-white p-3 text-sm">{notice}</p>}
    <MetaTestReceipt status={status} bindings={bindings} onRefresh={refresh}/>
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="rounded-xl border bg-white p-4">
        <h4 className="font-semibold">Bind one approved Meta form</h4>
        <p className="mt-1 text-xs text-slate-600">Only independently reviewed, non-paused campaigns can receive Meta form leads. Publishing the public website landing page is optional. A form may be bound only once.</p>
        <label className="mt-3 block text-sm">Reviewed campaign
          <select className={field} value={form.campaign_id} onChange={e=>setForm(f=>({...f,campaign_id:e.target.value}))}>
            <option value="">Choose active campaign</option>
            {approved.map(c=><option key={c.id} value={c.id}>{c.campaign_name||c.id}</option>)}
          </select>
        </label>
        {[["page_id","Meta Facebook Page ID"],["form_id","Meta Lead Form ID"],["review_reference","Independent form/privacy review reference"]].map(([key,label])=>
          <label key={key} className="mt-3 block text-sm">{label}
            <input className={field} value={form[key]} onChange={e=>setForm(f=>({...f,[key]:e.target.value}))}
              maxLength={key==="review_reference"?200:30} placeholder={key==="review_reference"?"Approved form-review reference":"Numeric ID"} />
          </label>)}
        <div className="mt-4 space-y-3 border-t pt-3 text-sm">
          <h5 className="font-semibold">Optional reviewed consent checkboxes</h5>
          <p className="text-xs text-slate-600">Use the exact checkbox keys from your Meta form and the independently approved text shown there. An affirmative checkbox is recorded as evidence, never as permission to resell to an unnamed buyer.</p>
          {[["sms_checkbox_key","Meta SMS checkbox key"],["sms_consent_text","Exact SMS consent disclosure"],["call_checkbox_key","Meta calling checkbox key"],["call_consent_text","Exact calling consent disclosure"]].map(([key,label])=><label key={key} className="block">{label}{key.endsWith("_text")
            ? <textarea rows={3} className={field} value={form[key]} onChange={e=>setForm(f=>({...f,[key]:e.target.value}))}/>
            : <input className={field} value={form[key]} onChange={e=>setForm(f=>({...f,[key]:e.target.value}))}/>}
          </label>)}
        </div>
        <button type="button" className={btn+" mt-4"} disabled={!!working||!form.campaign_id||
          !/^\d{5,30}$/.test(form.page_id)||!/^\d{5,30}$/.test(form.form_id)||form.review_reference.length<8}
          onClick={bind}>Save reviewed form mapping</button>
        {!approved.length&&<p className="mt-2 text-xs text-amber-800">First create an independently reviewed Final Expense campaign from Lead Marketplace.</p>}
      </div>
      <div className="rounded-xl border bg-white p-4">
        <h4 className="font-semibold">Connected form mappings</h4>
        {!bindings.filter(b=>b.mode!=="synthetic_test").length&&<p className="mt-3 text-sm text-slate-600">No Meta forms mapped yet.</p>}
        {bindings.filter(b=>b.mode!=="synthetic_test").map(b=><div key={b.id} className="mt-3 rounded-lg border p-3 text-xs">
          <div className="font-semibold">Form {b.form_id}</div>
          <div>Page {b.page_id} · Campaign {b.campaign_id.slice(0,8)}</div>
          <p className="mt-1 text-slate-600">Review: {b.review_reference}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" className={btn} disabled={!!working||!status?.ready}
              onClick={()=>inspectBinding(b)}>Verify Page/Form access (read-only)</button>
            <button type="button" className={btn} disabled={!!working||!status?.ready}
              onClick={()=>sync(b)}>Recover latest form leads (up to 25)</button>
          </div>
          {preflight[b.id]&&<div className="mt-2 space-y-1 rounded-lg bg-slate-50 p-2" data-testid="meta-preflight-results">
            <p>Meta form belongs to Page: <strong>{preflight[b.id].form_belongs_to_page?"Confirmed":"Not confirmed"}</strong></p>
            <p>Page subscribed to configured app: <strong>{preflight[b.id].page_subscribed_to_app?"Confirmed":"Not confirmed"}</strong></p>
            <p>End-to-end test lead delivery: <strong>Still requires a real Meta test submission</strong></p>
          </div>}
        </div>)}
      </div>
    </div>
    <div className="rounded-xl border bg-white p-4">
      <div className="flex items-center justify-between gap-2">
        <h4 className="font-semibold">Lead receipt activity</h4>
        <button type="button" className={btn} onClick={()=>act("refresh",async()=>{}, "Inbox refreshed.")} disabled={!!working}>Refresh</button>
      </div>
      {!events.length&&<p className="mt-3 text-sm text-slate-600">No Meta lead events received yet. Use Meta Lead Ads Testing after connecting the webhook and Page.</p>}
      {events.slice(0,30).map(e=><div className="mt-2 flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 text-xs" key={e.id}>
        <div className="min-w-0">
          <div className="font-semibold">{e.status} · Lead {e.leadgen_id}</div>
          <p className="text-slate-600">Form {e.form_id} · Attempts {e.attempts}</p>
          {e.error_code&&<p className="text-amber-900">Import issue: {e.error_code}</p>}
        </div>
        {(e.status==="failed"||e.status==="pending")&&<button type="button" className={btn}
          onClick={()=>retry(e)} disabled={!!working||!status?.ready}>Retry</button>}
      </div>)}
      <p className="mt-3 text-xs text-slate-600">Leads are held for human review. Contact and buyer-sharing permissions must be independently verified before follow-up or delivery.</p>
    </div>
  </section>;
}
